import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { TextField, Button } from '../../primitives';
import { getCsrfToken } from '../../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * FacilityForm
 * Reusable facility creation form with simple styling.
 *
 * - Can be embedded inside a Dialog or used standalone.
 * - By default it does NOT render actions. Set `showActions` for Submit button.
 * - Parent can trigger submission via `registerSubmit`.
 */
const FacilityForm = ({
  onSaved,
  showActions = false,
  submitLabel = 'Submit',
  registerSubmit,
  initialData = null,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Initialize form data if provided
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        country: initialData.country || 'United States',
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear general error message
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const submit = async () => {
    const requiredErrors = {};
    if (!formData.address_1?.trim()) requiredErrors.address_1 = 'Address 1 is required.';
    if (!formData.city?.trim()) requiredErrors.city = 'City is required.';
    if (!formData.state?.trim()) requiredErrors.state = 'State is required.';
    if (!formData.zip_code?.trim()) requiredErrors.zip_code = 'Zip is required.';
    if (!formData.country?.trim()) requiredErrors.country = 'Country is required.';

    if (Object.keys(requiredErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...requiredErrors }));
      setErrorMessage('Please fill in all required fields.');
      return false;
    }

    setLoading(true);
    setErrorMessage('');
    setErrors({});

    try {
      const requestData = {
        ...formData,
        // Ensure optional fields are sent as empty strings if not provided
        address_2: formData.address_2 || '',
        country: formData.country || 'United States',
      };

      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/facilities/`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (
          data.name ||
          data.address_1 ||
          data.address_2 ||
          data.city ||
          data.state ||
          data.zip_code ||
          data.country
        ) {
          setErrors(data);
        } else {
          setErrorMessage(
            data.error || data.message || 'Failed to create facility. Please try again.'
          );
        }
        setLoading(false);
        return false;
      }

      // Success - call parent callback with saved facility
      if (onSaved) onSaved(data);
      // Reset form
      setFormData({
        name: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'United States',
      });
      setLoading(false);
      return true;
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      console.error('Error creating facility:', err);
      setLoading(false);
      return false;
    }
  };

  // Let parent trigger submit
  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(submit);
  }, [registerSubmit, submit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submit();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Typography
        variant="body1"
        sx={{
          mb: 2,
          color: 'text.primary',
          fontWeight: 600,
        }}
      >
        Add a new facility:
      </Typography>

      <TextField
        fullWidth
        placeholder="Facility Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={!!errors.name}
        helperText={errors.name}
        margin="normal"
        autoComplete="off"
        autoFocus
      />

      <TextField
        fullWidth
        placeholder="Address 1"
        name="address_1"
        value={formData.address_1}
        onChange={handleChange}
        error={!!errors.address_1}
        helperText={errors.address_1}
        required
        margin="normal"
        autoComplete="street-address"
      />

      <TextField
        fullWidth
        placeholder="Address 2"
        name="address_2"
        value={formData.address_2}
        onChange={handleChange}
        error={!!errors.address_2}
        helperText={errors.address_2}
        margin="normal"
        autoComplete="address-line2"
      />

      <TextField
        fullWidth
        placeholder="City"
        name="city"
        value={formData.city}
        onChange={handleChange}
        error={!!errors.city}
        helperText={errors.city}
        required
        margin="normal"
        autoComplete="address-level2"
      />

      <TextField
        fullWidth
        placeholder="State"
        name="state"
        value={formData.state}
        onChange={handleChange}
        error={!!errors.state}
        helperText={errors.state}
        required
        margin="normal"
        autoComplete="address-level1"
      />

      <TextField
        fullWidth
        placeholder="Zip"
        name="zip_code"
        value={formData.zip_code}
        onChange={handleChange}
        error={!!errors.zip_code}
        helperText={errors.zip_code}
        required
        margin="normal"
        autoComplete="postal-code"
      />

      <FormControl fullWidth margin="normal" error={!!errors.country}>
        <InputLabel>Country</InputLabel>
        <Select
          name="country"
          value={formData.country}
          onChange={handleSelectChange}
          label="Country"
        >
          <MenuItem value="United States">United States</MenuItem>
        </Select>
        {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
      </FormControl>

      {showActions && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </Box>
            ) : (
              submitLabel
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FacilityForm;
